import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiPlus, FiMinus} from 'react-icons/fi';

const faqs = [
  {
    question: "Is this program medically supervised?",
    answer: "Yes. Every patient is reviewed by an independent, board-certified physician licensed in your state who oversees your protocol to ensure safety, monitor your progress, and maximize efficacy throughout the 6 weeks. JoinMyMD is a wellness coaching and administrative service; all medical decisions are made solely by your treating provider."
  },
  {
    question: "Do you prescribe weight loss medications?",
    answer: "Clinically appropriate prescription weight-loss medications may be prescribed when medically indicated. All prescribing decisions are made exclusively by the independent, licensed physician reviewing your case — not by JoinMyMD. If approved, medication is fulfilled and shipped directly to your door."
  },
  {
    question: "What exactly is the 6-Week Challenge?",
    answer: "It's a comprehensive, physician-guided weight loss protocol where we provide your exact nutrition framework, optional medical support, and monitor your progress daily to ensure you hit your goals. Individual results vary based on adherence, starting point, and medical history."
  },
  {
    question: "Do I have to cook complicated meals?",
    answer: "Not at all. Our nutrition frameworks are designed for busy people. We focus on simple, easy-to-prepare meals that fit into your lifestyle without requiring hours in the kitchen."
  },
  {
    question: "Is intense exercise required?",
    answer: "While movement is encouraged for overall health, our primary driver for fat loss is the nutrition protocol and medical support. We tailor activity recommendations to your current fitness level."
  },
  {
    question: "How much weight can I expect to lose?",
    answer: "Individual results vary based on adherence, starting weight, and medical history. Many clients experience meaningful progress within the 6-week period when following the protocol consistently. Results are not guaranteed and are dependent on individual factors."
  },
  {
    question: "How does the daily accountability work?",
    answer: "You'll have direct access to your clinical coaching team. You'll complete a quick daily check-in, and your coach will review your metrics, provide feedback, and make necessary adjustments to keep you on track."
  },
  {
    question: "What is your refund policy?",
    answer: "Program fees are refundable under our 6-Week Results Guarantee. If you follow the nutritional plan, log your meals, and engage with your coach throughout the six weeks without seeing measurable progress, you are entitled to a full refund or continued coaching at no charge. See our Refund Policy page for full terms."
  },
  {
    question: "What happens after the 6 weeks?",
    answer: "Once you complete the challenge, we provide a sustainable maintenance plan to ensure you keep the weight off for good. We also offer ongoing coaching for those who want to continue."
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section id="faq" className="py-20 md:py-32 bg-stone-950 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_0%,rgba(217,119,6,0.06),transparent)]" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14 md:mb-20">
          <motion.div initial={{opacity: 0, y: 20}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}}>
            <span className="inline-block px-5 py-2 rounded-full bg-stone-800 border border-stone-700 text-amber-400 text-sm font-bold uppercase tracking-widest mb-6">
              Got Questions?
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight font-serif leading-tight">
              Frequently Asked{' '}
              <span className="italic font-light text-stone-400">Questions</span>
            </h2>
          </motion.div>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{opacity: 0, y: 10}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: index * 0.04}}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${openIndex === index ? 'bg-stone-900 border-amber-500/30' : 'bg-stone-900/60 border-stone-800 hover:border-stone-700'}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full text-left px-6 py-6 md:px-8 md:py-7 flex justify-between items-center gap-4"
              >
                <span className="font-semibold text-lg md:text-xl text-stone-100 font-serif">{faq.question}</span>
                <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${openIndex === index ? 'bg-amber-500 text-white' : 'bg-stone-800 text-stone-400'}`}>
                  <SafeIcon icon={openIndex === index ? FiMinus : FiPlus} className="text-base" />
                </span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{height: 0, opacity: 0}}
                    animate={{height: 'auto', opacity: 1}}
                    exit={{height: 0, opacity: 0}}
                    transition={{duration: 0.3, ease: 'easeInOut'}}
                  >
                    <div className="px-6 pb-7 md:px-8 md:pb-8 text-stone-400 font-light leading-relaxed text-lg md:text-xl">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;